import { useCallback, useState } from "react";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";

export const ACTIVATION_ERROR = "Something went wrong. Please try again.";

/**
 * Hook for activating the Connect Services integration.
 *
 * POSTs to `/add_ons/activate_connect_service`, which provisions the Transactly
 * partner and creates the account's `transactly-connect-service` AccountProduct.
 * That single record is what gates both the Connect Services UI (via the layout's
 * `account_products.connect_service`) and the Connect Services API.
 *
 * Returns 201 when a partner was created, 200 when the account already had one,
 * and 502 when Transactly fails or times out. Both success codes are treated
 * identically here, which makes the call safely retryable.
 * `caseTransformingAxios` already attaches the CSRF token via its request
 * interceptor, so no body/token needs to be sent here.
 *
 * Deliberately Redux-free: the caller is responsible for reflecting
 * `didActivate` into Redux state.
 *
 * Returns:
 * - `activate` — triggers the activation request. Resolves `true` on success,
 *   `false` on failure. Never rejects.
 * - `error` — user-facing error message, or "" when there is nothing to show.
 * - `didActivate` — sticky; once a call succeeds it stays true.
 * - `resetError` — clears a stale error message without touching `didActivate`.
 *   Callers own the modal-open lifecycle, so this lets a reopen clear a
 *   leftover failure message from a previous attempt.
 */
export const useActivateConnectService = () => {
  const [error, setError] = useState("");
  const [didActivate, setDidActivate] = useState(false);

  const activate = async () => {
    setError("");

    try {
      await caseTransformingAxios.post("/add_ons/activate_connect_service");

      setDidActivate(true);
      return true;
    } catch {
      setError(ACTIVATION_ERROR);
      return false;
    }
  };

  const resetError = useCallback(() => setError(""), []);

  return { activate, error, didActivate, resetError };
};

export default useActivateConnectService;
